import { ErrorNotice } from "../ui/ErrorNotice.js";
/**
 * VersionHistoryPanel — the admin TemplateEditor's version genealogy (item 5B).
 *
 * Lists every version via `client.listVersions(templateId)` newest-first with a
 * status badge, and offers the two real, already-shipped lifecycle actions:
 *  - "Rollback to vN" (`client.rollbackVersion`) for an Approved/Active version —
 *    re-activates that prior version (spec 018 / §4.3).
 *  - "Reopen as draft" (`client.reopenVersion`) for a Rejected/Archived version —
 *    clones it into a fresh editable Draft (spec 018 / §4.1).
 *
 * No new endpoints; this is pure wiring of existing-but-unused client methods.
 * After a successful action it re-lists + calls `onChanged` so the parent editor
 * re-fetches the template (the current version may now differ).
 */
import { useCallback, useEffect, useState } from "react";
import { useServices } from "../app/context.js";
import { serverTsDate } from "@genz/platform";
import type { TemplateStatus, TemplateVersion } from "@genz/platform";
import { statusBadgeClass, statusLabel } from "./templateLabels.js";
import { RowAction } from "../ui/RowAction.js";

export interface VersionHistoryPanelProps {
  templateId: string;
  /** Bumped by the parent after a save so the list re-fetches (e.g. a new draft). */
  refreshKey?: number;
  /** Called after a rollback/reopen so the parent can re-fetch the template. */
  onChanged?: () => void;
  /**
   * WS-Z6 — the panel is a section BODY inside the editor's one accordion, and an accordion
   * head has to say what its closed section holds. The panel still owns the fetch, so it is
   * the only thing that knows the count; it reports it here rather than the page re-listing
   * versions on its own. `null` while the first load is in flight.
   */
  onCount?: (n: number | null) => void;
}

const ROLLBACKABLE: ReadonlySet<TemplateStatus> = new Set(["Approved", "Active"]);
const REOPENABLE: ReadonlySet<TemplateStatus> = new Set(["Rejected", "Archived"]);

/**
 * A version payload as ONE canonical string: object keys sorted at every depth, arrays left in
 * order (order is meaning in a `views` list), `undefined` distinguished from every real value.
 *
 * This is the "payload hash" the untouched-draft check compares, and it is the STRING rather
 * than a digest of it on purpose: the payloads are a few kilobytes, a string comparison cannot
 * collide, and `crypto.subtle.digest` is async — which would turn a render-time decision into a
 * state machine for nothing. Key order matters because nothing guarantees two rows of the same
 * JSON come back from the server, or through the client mapper, in the same order; comparing the
 * raw `JSON.stringify` would report an edit every time anything re-serialised the record.
 */
function canonicalPayload(value: unknown): string {
  const walk = (v: unknown): unknown => {
    if (Array.isArray(v)) return v.map(walk);
    if (v && typeof v === "object")
      return Object.fromEntries(
        Object.entries(v as Record<string, unknown>)
          .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
          .map(([k, x]) => [k, walk(x)]),
      );
    return v;
  };
  return JSON.stringify(walk(value));
}

/**
 * THE VERSION A DRAFT WAS SEEDED FROM, resolved the way the server seeds it.
 *
 * `cloud/app/catalog/service.py::new_draft_version` branches from `_current_version` — the
 * template's `current_version_id`, i.e. the ACTIVE version — and falls back to the latest row
 * only when there is no current one. That distinction is load-bearing after a rollback, where
 * the Active version is NOT the highest-numbered one: the version that was rolled away sits
 * above it, and a check that compared a draft with its immediate predecessor would call an
 * untouched draft edited. Candidates are restricted to versions BELOW the draft, because a
 * draft cannot have been seeded from a row created after it.
 */
function parentOf(draft: TemplateVersion, versions: TemplateVersion[]): TemplateVersion | null {
  const below = versions.filter((v) => v.versionNumber < draft.versionNumber);
  return (
    below.find((v) => v.status === "Active") ??
    [...below].sort((a, b) => b.versionNumber - a.versionNumber)[0] ??
    null
  );
}

/**
 * WS-LEFT — the version ids of every Draft that is byte-identical to the version it was opened
 * from, i.e. every draft the editor created just by being opened.
 *
 * WHY THE ROW NEEDS SAYING. Opening the editor on an Active template CREATES a version:
 * `new_draft_version` clones the current one and reuses an existing Draft rather than stacking
 * them, so re-opening is idempotent — but the history still grows a row that looks exactly like
 * a revision somebody made. `v4 Draft` under `v3 Active` cannot be told apart from real work in
 * progress, and an operator deciding whether to roll back is reading it.
 *
 * NO EVIDENCE MEANS NO CLAIM. A payload missing on either side — `undefined`, or an explicit
 * `null` off the wire (F-6) — leaves the draft labelled `Draft`. Saying "no changes yet" about a
 * draft that may hold an hour's work is the worse of the two errors, so the check only speaks
 * when it has both payloads in hand.
 *
 * Exported for the suite, which is where the rollback case and the key-order case are pinned.
 */
export function untouchedDraftIds(versions: TemplateVersion[]): Set<string> {
  const out = new Set<string>();
  for (const v of versions) {
    /* F-6: `== null` catches `null` as well as `undefined`. A JSON null crosses the wire as
       `null` and `wireVersionToView` passes it straight through; `canonicalPayload(null)` is
       the string `"null"`, so two rows the server sent no payload for compared EQUAL and an
       ordinary edited draft was labelled "no changes yet" — the one error this check exists to
       avoid, produced by exactly the rows it was written to be careful about. */
    if (v.status !== "Draft" || v.payload == null) continue;
    const parent = parentOf(v, versions);
    if (!parent || parent.payload == null) continue;
    if (canonicalPayload(v.payload) === canonicalPayload(parent.payload)) out.add(v.versionId);
  }
  return out;
}

export function VersionHistoryPanel({ templateId, refreshKey, onChanged, onCount }: VersionHistoryPanelProps) {
  const { client } = useServices();
  const [versions, setVersions] = useState<TemplateVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const rows = await client.listVersions(templateId);
      // Newest first — version numbers are monotonic.
      setVersions([...rows].sort((a, b) => b.versionNumber - a.versionNumber));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [client, templateId]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!cancelled) await load();
    })();
    return () => {
      cancelled = true;
    };
  }, [load, refreshKey]);

  // Report the count to the accordion head. Separate from `load` so a re-list that returns
  // the same rows does not re-notify, and so an error (which leaves the list as it was)
  // does not report a stale-looking zero.
  useEffect(() => {
    onCount?.(loading ? null : versions.length);
  }, [onCount, loading, versions.length]);

  /* Derived, not stored: the list is the only input, so a re-list after a save re-decides it
     and the label flips to plain `Draft` on the first edit that lands. */
  const untouchedIds = untouchedDraftIds(versions);

  const rollback = useCallback(
    async (v: TemplateVersion) => {
      setBusy(v.versionNumber);
      setError(null);
      try {
        await client.rollbackVersion(templateId, v.versionNumber);
        await load();
        onChanged?.();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setBusy(null);
      }
    },
    [client, templateId, load, onChanged],
  );

  const reopen = useCallback(
    async (v: TemplateVersion) => {
      setBusy(v.versionNumber);
      setError(null);
      try {
        await client.reopenVersion(templateId, v.versionNumber);
        await load();
        onChanged?.();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setBusy(null);
      }
    },
    [client, templateId, load, onChanged],
  );

  return (
    /* WS-Z6 / R12: the private `<details>` is gone — this is the BODY of a section in the
       editor's one `ui/Accordion.tsx`, whose head carries the label and the count. */
    <div className="tpl-editor__section-body" data-testid="version-history">
      {error && (
        <p className="tpl-editor__error" role="alert" data-testid="version-history-error">
          <ErrorNotice detail={error} />
        </p>
      )}

      {loading ? (
        <p className="faint" data-testid="version-history-loading">
          Loading versions…
        </p>
      ) : versions.length === 0 ? (
        <p className="faint" data-testid="version-history-empty">
          No versions yet.
        </p>
      ) : (
        <ul className="tpl-editor__versions" data-testid="version-history-list">
          {versions.map((v) => {
            const canRollback = ROLLBACKABLE.has(v.status);
            const canReopen = REOPENABLE.has(v.status);
            /* The draft the editor made by being opened. It stays IN the list and in the head's
               count — the panel lists what exists — but it stops dressing as a revision. */
            const untouched = untouchedIds.has(v.versionId);
            return (
              <li
                key={v.versionNumber}
                className={`tpl-editor__version${untouched ? " tpl-editor__version--untouched" : ""}`}
                data-testid="version-row"
                data-untouched={untouched ? "true" : undefined}
              >
                <div className="tpl-editor__version-head">
                  <span className="tpl-editor__version-num">v{v.versionNumber}</span>
                  {/* LABEL and CLASS both from the formatter layer (review-1 m-3 + review-2
                      n-1): the header pill two cards above this list and this row now paint
                      one state one colour. */}
                  <span
                    className={`badge ${untouched ? "badge--neutral" : statusBadgeClass(v.status)}`}
                    data-testid="version-status"
                  >
                    {untouched ? "Draft · no changes yet" : statusLabel(v.status)}
                  </span>
                  {v.activeFrom && (
                    <span className="faint">
                      active {serverTsDate(v.activeFrom)?.toLocaleString() ?? "—"}
                    </span>
                  )}
                </div>
                {(v.submittedBy || v.approvedBy) && (
                  // Short ids only — a raw UUID per row read as debug output. The full value
                  // stays one hover away; "system:auto" reads as what it is.
                  <p className="faint tpl-editor__version-meta">
                    {v.submittedBy && (
                      <>submitted by <span title={v.submittedBy}>{v.submittedBy === "system:auto" ? "auto" : v.submittedBy.slice(0, 8)}</span></>
                    )}
                    {v.submittedBy && v.approvedBy && " · "}
                    {v.approvedBy && (
                      <>approved by <span title={v.approvedBy}>{v.approvedBy === "system:auto" ? "auto" : v.approvedBy.slice(0, 8)}</span></>
                    )}
                  </p>
                )}
                <div className="row tpl-editor__version-actions">
                  {canRollback && (
                    <RowAction
                      label={busy === v.versionNumber ? "Rolling back…" : `Rollback to v${v.versionNumber}`}
                      icon="undo"
                      onClick={() => void rollback(v)}
                      disabled={busy === v.versionNumber}
                      busy={busy === v.versionNumber}
                      data-testid="version-rollback"
                    />
                  )}
                  {canReopen && (
                    <RowAction
                      label={busy === v.versionNumber ? "Reopening…" : "Reopen as draft"}
                      hint="Take this version back into an editable draft"
                      icon="redo"
                      onClick={() => void reopen(v)}
                      disabled={busy === v.versionNumber}
                      busy={busy === v.versionNumber}
                      data-testid="version-reopen"
                    />
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
